import React, { useState, useEffect, memo } from 'react';
import ReactDOM from 'react-dom';
import { skinLoaderService } from '../../services/skinLoaderService';

// Storage for external component skin configurations
const componentSkins = new Map();

// Storage for loaded assets to prevent duplicate loading
const loadedAssets = {
  css: new Set(),
  js: new Set(),
  umd: new Set()
};

// 컴포넌트 캐시 강화
const componentCache = new Map();

// Map component types to available skins
const componentTypeSkins = new Map();

// Cache for external skin wrapper components
const externalSkinWrapperCache = new Map();

// Track active skins
const activeSkins = new Set();

// 전역 스킨 레지스트리 시스템
window.WithCookieSkinRegistry = window.WithCookieSkinRegistry || {
  skins: new Map(),
  callbacks: new Map(),
  
  register(skinId, component) {
    this.skins.set(skinId, component);
    
    // 대기 중인 콜백이 있으면 실행
    if (this.callbacks.has(skinId)) {
      const callbacks = this.callbacks.get(skinId);
      callbacks.forEach(cb => cb(component));
      this.callbacks.delete(skinId);
    }
  },
  
  waitFor(skinId, callback) {
    // 이미 등록된 스킨이면 즉시 콜백 실행
    if (this.skins.has(skinId)) {
      callback(this.skins.get(skinId));
    } else {
      // 아직 등록되지 않았으면 콜백 대기열에 추가
      if (!this.callbacks.has(skinId)) {
        this.callbacks.set(skinId, []);
      }
      this.callbacks.get(skinId).push(callback);
    }
  },
  
  has(skinId) {
    return this.skins.has(skinId);
  },
  
  get(skinId) {
    return this.skins.get(skinId);
  }
};

/**
 * Register an external component skin
 * @param {import('../../types/component-skin').ComponentSkinConfig} config 
 */
export function registerComponentSkin(config) {
  if (!config.id || !config.umdUrl || !config.globalName || !config.componentTypes) {
    throw new Error('Component skin must have id, umdUrl, globalName, and componentTypes');
  }
  
  // Store the skin configuration
  componentSkins.set(config.id, {
    ...config,
    loaded: false,
    loading: false,
    component: null,
    error: null
  });
  
  // Map skin to component types
  config.componentTypes.forEach(componentType => {
    if (!componentTypeSkins.has(componentType)) {
      componentTypeSkins.set(componentType, new Set());
    }
    componentTypeSkins.get(componentType).add(config.id);
  });
  
}

/**
 * Get all available skins for a component type
 * @param {string} componentType 
 * @returns {Array<{id: string, name: string, preview?: string}>}
 */
export function getSkinsForComponent(componentType) {
  const skinIds = componentTypeSkins.get(componentType) || new Set();
  const skins = [];
  
  skinIds.forEach(skinId => {
    const skin = componentSkins.get(skinId);
    if (skin) {
      skins.push({
        id: skin.id,
        name: skin.name,
        preview: skin.preview,
        description: skin.description,
        author: skin.author,
        version: skin.version
      });
    }
  });
  
  return skins;
}

/**
 * Get all registered component skins
 * @returns {Array<import('../../types/component-skin').ComponentSkinConfig>}
 */
export function getAllComponentSkins() {
  return Array.from(componentSkins.values()).map(skin => ({
    id: skin.id,
    name: skin.name,
    componentTypes: skin.componentTypes,
    preview: skin.preview,
    description: skin.description,
    author: skin.author,
    version: skin.version,
    loaded: skin.loaded,
    error: skin.error
  }));
}

/**
 * Check if a skin ID is external
 * @param {string} skinId 
 * @returns {boolean}
 */
export function isExternalSkin(skinId) {
  // "basic" is always an internal skin
  if (skinId === 'basic') {
    return false;
  }
  
  // Check if it's a manually registered skin
  if (componentSkins.has(skinId)) {
    return true;
  }
  
  // Check if it's a numeric ID (API-based external skin)
  const numericId = parseInt(skinId, 10);
  if (!isNaN(numericId) && numericId.toString() === skinId.toString()) {
    return true;
  }
  
  return false;
}

/**
 * Load CSS file with CORS support and version-based caching
 * @param {string} url 
 * @param {string} skinId - Skin ID to scope the CSS
 * @returns {Promise<void>}
 */
async function loadCssFile(url, skinId = null) {
  // 버전 기반 캐시 키 생성
  const versionMatch = url.match(/v=([^&]+)/);
  const version = versionMatch ? versionMatch[1] : 'unknown';
  const cacheKey = skinId ? `${url}-${skinId}-${version}` : `${url}-${version}`;
  
  // 이미 로드된 동일 버전은 스킵
  if (loadedAssets.css.has(cacheKey)) {

    // 스킨별 CSS는 활성화 상태 확인
    if (skinId) {
      const existingLink = document.querySelector(`link[data-skin-id="${skinId}"][data-version="${version}"]`);
      if (existingLink && existingLink.disabled) {
        existingLink.disabled = false;
      }
    }
    return;
  }
  
  // 구버전 CSS 제거
  if (skinId) {
    const oldLinks = document.querySelectorAll(`link[data-skin-id="${skinId}"]:not([data-version="${version}"])`);
    oldLinks.forEach(link => {
      link.remove();
    });
  }
  
  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.crossOrigin = 'anonymous';
    
    // Add data attributes to identify skin-specific CSS
    if (skinId) {
      link.setAttribute('data-skin-id', skinId);
      link.setAttribute('data-skin-css', 'true');
      link.setAttribute('data-version', version);
    }
    
    link.onload = () => {
      loadedAssets.css.add(cacheKey);

      // Disable CSS initially if it's for a specific skin
      if (skinId && !activeSkins.has(skinId)) {
        link.disabled = true;
      }
      
      resolve();
    };
    
    link.onerror = (error) => {
      console.error(`[CSS] 로드 에러 (${url}):`, error);
      // CSS 실패해도 계속 진행
      loadedAssets.css.add(cacheKey);
      resolve();
    };
    
    document.head.appendChild(link);
  });
}

/**
 * Load JS file
 * @param {string} url 
 * @returns {Promise<void>}
 */
async function loadJsFile(url) {
  if (loadedAssets.js.has(url)) {
    return;
  }
  
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.crossOrigin = 'anonymous';
    
    script.onload = () => {
      loadedAssets.js.add(url);
      resolve();
    };
    
    script.onerror = () => {
      reject(new Error(`Failed to load JS: ${url}`));
    };
    
    document.body.appendChild(script);
  });
}

/**
 * Load UMD script and get the exported component
 * @param {string} url 
 * @param {string} globalName 
 * @param {string} skinId - 스킨 ID (레지스트리 사용)
 * @returns {Promise<React.ComponentType>}
 */
async function loadUMDScript(url, globalName, skinId = null) {
  // 컴포넌트 스킨의 경우 캐싱 스킵 (독립성 보장)
  const isApiBasedSkin = url.includes('cdn.withcookie-refrigerator.com') || 
                        url.includes('withcookie-bucket.s3.ap-northeast-2.amazonaws.com/skins/') ||
                        url.includes('withcookie-bucket.s3.ap-northeast-2.amazonaws.com/skin/') ||
                        url.includes('withcookie.b-cdn.net/skins/');
  

  if (!isApiBasedSkin) {
    // Check if already loaded (일반 외부 스킨만)
    if (window[globalName]) {
      return window[globalName];
    }
    
    // Check if already loading (일반 외부 스킨만)
    if (loadedAssets.umd.has(url)) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (window[globalName]) {
            clearInterval(checkInterval);
            resolve(window[globalName]);
          }
        }, 100);
      });
    }
  }
  
  // API 기반 스킨이 아닌 경우만 로딩 에셋에 추가
  if (!isApiBasedSkin) {
    loadedAssets.umd.add(url);
  }
  
  // Ensure React and ReactDOM are available globally
  if (!window.React) {
    window.React = React;
  }
  if (!window.ReactDOM) {
    window.ReactDOM = ReactDOM;
  }
  
  // UMD 환경 설정 - 외부 스킨이 CommonJS나 AMD로 빌드된 경우를 위해
  if (!window.module) {
    window.module = { exports: {} };
  }
  if (!window.exports) {
    window.exports = window.module.exports;
  }
  
  // CommonJS require polyfill
  if (!window.require) {
    window.require = function(module) {
      if (module === 'react' || module === 'React') {
        return React;
      }
      if (module === 'react-dom' || module === 'ReactDOM') {
        return ReactDOM;
      }
      console.warn(`[require polyfill] Unknown module requested: ${module}`);
      return {};
    };
  }

  return new Promise((resolve, reject) => {
    // 타임아웃 설정 (10초)
    const timeout = setTimeout(() => {
      console.error(`[loadUMDScript] 스킨 로드 타임아웃: ${skinId || globalName}`);
      reject(new Error(`스킨 로드 타임아웃: ${skinId || globalName} (10초 초과)`));
    }, 10000);
    
    const script = document.createElement('script');
    
    // API 기반 스킨의 경우 버전이 URL에 포함되어 있으므로 타임스탬프 불필요
    // 개발 모드나 강제 새로고침이 필요한 경우에만 타임스탬프 추가
    if (isApiBasedSkin && window.location.search.includes('forceFresh=true')) {
      const separator = url.includes('?') ? '&' : '?';
      script.src = `${url}${separator}_t=${Date.now()}`;
    } else {
      script.src = url;
    }
    
    script.type = 'text/javascript';
    // 브라우저 최적화 힌트
    script.async = true;
    script.defer = false;
    
    // CORS 제거하여 속도 개선 시도
    if (!isApiBasedSkin) {
      script.crossOrigin = 'anonymous';
    }
    
    // 스크립트 로드 전에 module.exports 초기화 및 require polyfill 설정
    const previousExports = window.module ? window.module.exports : null;
    const previousRequire = window.require;
    const previousDefine = window.define;
    
    window.module = { exports: {} };
    window.exports = window.module.exports;
    
    // 스킨 컴포넌트를 저장할 변수
    let amdComponent = null;
    
    // AMD define polyfill
    window.define = function(deps, factory) {

      if (typeof deps === 'function') {
        // define(factory) 형태
        factory = deps;
        deps = [];
      }
      
      if (typeof factory === 'function') {
        const resolvedDeps = deps.map(dep => {
          if (dep === 'react') return React;
          if (dep === 'react-dom') return ReactDOM;
          if (dep === 'require') return window.require;
          if (dep === 'exports') return window.exports;
          if (dep === 'module') return window.module;
          return null;
        });
        
        const result = factory(...resolvedDeps);
        if (result) {
          amdComponent = result.default || result;
          // 전역 변수로도 설정
          window[globalName] = amdComponent;
          if (skinId) {
            window[`WithCookieSkin_${skinId}`] = amdComponent;
          }
        }
      }
    };
    window.define.amd = true;
    
    // require polyfill - 스크립트 실행 전에 설정
    window.require = function(moduleName) {
      if (moduleName === 'react' || moduleName === 'React') {
        return React;
      }
      if (moduleName === 'react-dom' || moduleName === 'ReactDOM' || moduleName === 'react-dom/client') {
        return ReactDOM;
      }
      // 기타 일반적인 모듈들
      if (moduleName === 'classnames' || moduleName === 'clsx') {
        // 간단한 classnames 구현
        return function(...args) {
          return args.filter(Boolean).join(' ');
        };
      }
      if (moduleName === 'prop-types') {
        // prop-types는 production에서는 필요없으므로 빈 객체 반환
        return {};
      }
      console.warn(`[require polyfill] Unknown module requested: ${moduleName}`);
      return {};
    };
    
    script.onload = () => {

      // API 기반 스킨의 경우 전역 변수 확인 후 바로 반환
      if (isApiBasedSkin && skinId) {
        // 가능한 전역 변수 이름들 확인
        const possibleNames = [
          globalName,
          `WithCookieSkin_${skinId}`,
          `WithCookieSkin${skinId}`,
          `ExternalSkin${skinId}`,
          globalName.replace(/_\d+_\d+_\d+$/, ''), // 버전 제거
          // 추가 패턴들
          `Skin${skinId}`,
          `skin${skinId}`,
          skinId // 스킨 ID 자체
        ];
        

        // 전역 변수 검색 함수
        let skinRelatedGlobals = []; // 함수 외부에서도 접근 가능하도록 변수 선언
        
        const findGlobalComponent = (attemptNumber = 1) => {

          
          // 첫 번째 시도에서만 전체 스킨 관련 변수 로깅
          if (attemptNumber === 1) {
            skinRelatedGlobals = Object.keys(window).filter(key => 
              key.toLowerCase().includes('skin') || 
              key.includes(skinId) ||
              key.includes('WithCookie')
            );
            

          }
          
          let foundComponent = null;
          let foundName = null;
          
          for (const name of possibleNames) {
            if (window[name]) {
              foundComponent = window[name];
              foundName = name;
              break;
            }
          }
          
          // module.exports 확인
          if (!foundComponent && window.module && window.module.exports) {
            const exports = window.module.exports;

            if (typeof exports === 'function') {
              foundComponent = exports;
              foundName = 'module.exports';
            } else if (exports && typeof exports === 'object') {
              // default export 확인
              if (exports.default && typeof exports.default === 'function') {
                foundComponent = exports.default;
                foundName = 'module.exports.default';
              }
              // 또는 직접 컴포넌트 이름으로 export된 경우
              else if (exports[globalName] && typeof exports[globalName] === 'function') {
                foundComponent = exports[globalName];
                foundName = `module.exports.${globalName}`;
              }
              // 또는 skinId로 export된 경우
              else if (exports[`WithCookieSkin_${skinId}`] && typeof exports[`WithCookieSkin_${skinId}`] === 'function') {
                foundComponent = exports[`WithCookieSkin_${skinId}`];
                foundName = `module.exports.WithCookieSkin_${skinId}`;
              }
              // 첫 번째 함수형 export를 사용
              else {
                const functionExports = Object.entries(exports).filter(([key, value]) => typeof value === 'function');
                if (functionExports.length > 0) {
                  const [key, value] = functionExports[0];
                  foundComponent = value;
                  foundName = `module.exports.${key}`;
                }
              }
            }
          }
          
          if (foundComponent) {
            // module.exports를 사용한 경우 전역 변수로도 설정
            if (foundName.includes('module.exports')) {
              window[globalName] = foundComponent;
            }
            clearTimeout(timeout);
            resolve(foundComponent);
          } else if (attemptNumber < 10) {
            // 최대 10번까지 재시도, 매우 빠르게
            const delay = attemptNumber < 3 ? 0 : attemptNumber < 5 ? 5 : 10; // 처음 3번은 즉시, 다음 2번은 5ms, 나머지는 10ms
            if (delay === 0) {
              findGlobalComponent(attemptNumber + 1);
            } else {
              setTimeout(() => findGlobalComponent(attemptNumber + 1), delay);
            }
          } else {
            clearTimeout(timeout);
            
            // 마지막 시도: 모든 전역 변수 중에서 React 컴포넌트 찾기
            const allGlobalKeys = Object.keys(window);
            let lastAttemptComponent = null;
            
            for (const key of allGlobalKeys) {
              try {
                const value = window[key];
                // React 컴포넌트인지 확인 (함수이고, React element를 반환하는지)
                if (typeof value === 'function' && value.prototype && 
                    (value.prototype.isReactComponent || 
                     (value.toString && value.toString().includes('createElement')) ||
                     (value.toString && value.toString().includes('jsx')))) {
                  if (key.includes(skinId) || key.toLowerCase().includes('skin')) {
                    lastAttemptComponent = value;
                    break;
                  }
                }
              } catch (e) {
                // 접근할 수 없는 속성은 무시
              }
            }
            
            if (lastAttemptComponent) {
              resolve(lastAttemptComponent);
              return;
            }
            
            // 디버깅을 위해 스크립트 내용 일부 확인
            fetch(script.src)
              .then(res => res.text())

              .catch(err => console.error(`[loadUMDScript] 스크립트 내용 확인 실패:`, err));
            
            console.error(`[loadUMDScript] 전역 변수를 찾을 수 없음:`, {
              globalName,
              skinId,
              possibleNames,
              allGlobals: skinRelatedGlobals,
              moduleExports: window.module ? window.module.exports : null
            });
            reject(new Error(`전역 변수를 찾을 수 없음: ${globalName}`));
          }
        };
        
        // 스크립트 실행 직후 즉시 시도
        findGlobalComponent(1);
        
        return;
      }
      
      // 일반 스킨의 경우 기존 폴링 방식 사용
      clearTimeout(timeout);
      
      // Check for multiple possible global name variations
      const possibleNames = [
        globalName,
        globalName.charAt(0).toLowerCase() + globalName.slice(1),
        globalName.charAt(0).toUpperCase() + globalName.slice(1),
        `${globalName}Component`,
        `${globalName}Skin`,
        globalName.replace('Component', ''),
        globalName.replace('Skin', '')
      ];
      
      // 컴포넌트 찾기 함수
      const findComponent = () => {
        let foundComponent = null;
        let foundName = null;
        
        // 1. 먼저 예상된 이름들로 시도
        for (const name of possibleNames) {
          if (window[name]) {
            foundComponent = window[name];
            foundName = name;
            break;
          }
        }
        
        return { foundComponent, foundName };
      };
      
      // 컴포넌트 처리 함수
      const processComponent = (foundComponent, foundName) => {
        if (!foundComponent) return false;
        

        if (typeof foundComponent === 'function') {
          resolve(foundComponent);
          return true;
        } else if (foundComponent && typeof foundComponent === 'object') {
          // If it's an object, check for common export patterns
          const patterns = ['default', 'component', 'Component'];
          let actualComponent = null;
          
          for (const pattern of patterns) {
            if (foundComponent[pattern] && typeof foundComponent[pattern] === 'function') {
              actualComponent = foundComponent[pattern];
              break;
            }
          }
          
          if (actualComponent) {
            resolve(actualComponent);
            return true;
          }
        }
        
        return false;
      };
      
      // 즉시 확인 + 폴링
      const attemptLoad = () => {
        const { foundComponent, foundName } = findComponent();
        if (processComponent(foundComponent, foundName)) return true;
        return false;
      };
      
      // 1단계: 즉시 확인
      if (attemptLoad()) return;
      
      // 2단계: 폴링 (5ms 간격, 최대 10회 = 50ms)
      let attempts = 0;
      const maxAttempts = 10;
      
      const poll = () => {
        if (attemptLoad()) return;
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5);
        } else {
          console.error(`[loadUMDScript] 전역 변수를 찾을 수 없음: ${globalName}`);
          reject(new Error(`전역 변수를 찾을 수 없음: ${globalName}`));
        }
      };
      
      setTimeout(poll, 5);
    };
    
    script.onerror = (event) => {
      clearTimeout(timeout);
      console.error('[loadUMDScript] 스크립트 로드 에러:', { url, event });
      
      // Try loading without CORS as fallback
      if (script.crossOrigin !== undefined) {
        if (!isApiBasedSkin) {
          loadedAssets.umd.delete(url);
        }
        
        const fallbackScript = document.createElement('script');
        fallbackScript.src = isApiBasedSkin ? script.src : url;
        fallbackScript.type = 'text/javascript';
        
        fallbackScript.onload = script.onload;
        fallbackScript.onerror = () => {
          clearTimeout(timeout);
          if (!isApiBasedSkin) {
            loadedAssets.umd.delete(url);
          }
          reject(new Error(`스크립트 로드 실패 (CORS 없이도 실패): ${url}`));
        };
        
        document.body.appendChild(fallbackScript);
        return;
      }
      
      if (!isApiBasedSkin) {
        loadedAssets.umd.delete(url);
      }
      reject(new Error(`스크립트 로드 실패: ${url}`));
    };
    
    document.body.appendChild(script);
  });
}

/**
 * Load a component skin by ID
 * @param {string} skinId 
 * @returns {Promise<React.ComponentType>}
 */
export async function loadComponentSkin(skinId) {

  // API 기반 스킨은 캐시하지 않음 (독립성 보장)
  const numericId = parseInt(skinId, 10);
  const isApiSkin = !isNaN(numericId) && numericId.toString() === skinId.toString();
  
  if (!isApiSkin && componentCache.has(skinId)) {
    return componentCache.get(skinId);
  }
  
  let loadPromise;
  
  // Check if it's a manually registered skin
  const config = componentSkins.get(skinId);
  
  if (config) {
    // Handle manually registered skin (existing logic)
    loadPromise = loadManuallyRegisteredSkin(config, skinId);
  } else {
    // Check if it's an API-based skin (numeric ID)
    const numericId = parseInt(skinId, 10);
    if (!isNaN(numericId) && numericId.toString() === skinId.toString()) {
      loadPromise = loadApiBasedSkin(skinId);
    } else {
      throw new Error(`Component skin "${skinId}" not found`);
    }
  }
  
  // 로딩 중인 Promise를 캐시하여 중복 로딩 방지 (API 스킨 제외)
  if (!isApiSkin) {
    componentCache.set(skinId, loadPromise);
  }
  
  try {
    const component = await loadPromise;
    // 성공 시 결과를 캐시에 저장 (API 스킨 제외)
    if (!isApiSkin) {
      componentCache.set(skinId, component);
    }
    return component;
  } catch (error) {
    // 실패 시 캐시에서 제거
    if (!isApiSkin) {
      componentCache.delete(skinId);
    }
    throw error;
  }
}

/**
 * Load manually registered component skin
 * @param {Object} config 
 * @param {string} skinId 
 * @returns {Promise<React.ComponentType>}
 */
async function loadManuallyRegisteredSkin(config, skinId) {
  
  // Return cached component if already loaded
  if (config.loaded && config.component) {
    return config.component;
  }
  
  // Prevent concurrent loading
  if (config.loading) {
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (config.loaded && config.component) {
          clearInterval(checkInterval);
          resolve(config.component);
        } else if (config.error) {
          clearInterval(checkInterval);
          reject(config.error);
        }
      }, 100);
    });
  }
  
  config.loading = true;
  
  try {
    // Load CSS files with skin ID for scoping
    if (config.cssUrls && config.cssUrls.length > 0) {
      try {
        await Promise.all(config.cssUrls.map(url => loadCssFile(url, config.id)));
      } catch (cssError) {
        console.warn('CSS loading failed, continuing:', cssError);
      }
    }
    
    // Load JS dependencies
    if (config.jsUrls && config.jsUrls.length > 0) {
      for (const url of config.jsUrls) {
        await loadJsFile(url);
      }
    }
    
    // Load the main UMD bundle
    const Component = await loadUMDScript(config.umdUrl, config.globalName);
    
    config.component = Component;
    config.loaded = true;
    config.loading = false;
    

    return Component;
  } catch (error) {
    config.error = error;
    config.loading = false;
    console.error(`Failed to load component skin "${config.name}":`, error);
    throw error;
  }
}

/**
 * Load API-based component skin
 * @param {string} skinId 
 * @returns {Promise<React.ComponentType>}
 */
async function loadApiBasedSkin(skinId) {

  // 싱글톤 서비스 사용
  return skinLoaderService.loadSkin(skinId, async () => {
    try {
      // Import API function dynamically to avoid circular dependencies
      const { fetchSkinDetail } = await import('../../services/skinApi');
      
      // Fetch skin details from API
      const skinData = await fetchSkinDetail(skinId);

      // Handle different API response structures
      let latestVersion;
      if (skinData.latestVersion) {
        // Direct latestVersion structure
        latestVersion = skinData.latestVersion;
      } else if (skinData.skin && skinData.skin.versions && skinData.skin.versions.length > 0) {
        // Nested skin.versions structure - use the first/latest version
        latestVersion = skinData.skin.versions[0];
      } else {
        throw new Error(`스킨 데이터를 찾을 수 없거나 잘못된 구조입니다. ID: ${skinId}`);
      }
      
      // Load JS file (UMD format)
      if (!latestVersion.jsFileUrl) {
        throw new Error(`JS 파일 URL이 없습니다. 스킨 ID: ${skinId}`);
      }
      
      // Generate a unique global name for this skin
      const actualSkinData = skinData.skin || skinData;
      const skinName = actualSkinData.name || `Skin_${skinId}`;
      const versionString = latestVersion.version || '1_0_0';
      const globalName = `WithCookieSkin_${skinId}_${versionString.replace(/\./g, '_')}`;
      

      // 1. CSS 먼저 로드 (실패해도 계속)
      if (latestVersion.cssFileUrl) {
        try {
          await loadCssFile(latestVersion.cssFileUrl, skinId);
        } catch (cssError) {
          console.warn(`[loadApiBasedSkin] CSS 로드 실패, JS는 계속 로드: ${skinId}`, cssError);
        }
      }
      
      // 2. JS 로드 (레지스트리 사용)
      // 다음 스킨들을 미리 프리페치
      if (typeof window !== 'undefined' && window.requestIdleCallback) {
        window.requestIdleCallback(() => {
          const nextSkinIds = ['16', '17', '18']; // 자주 사용되는 다른 스킨들
          nextSkinIds.forEach(nextId => {
            if (nextId !== skinId) {
              skinLoaderService.preloadSkin(nextId);
            }
          });
        });
      }
      
      const Component = await loadUMDScript(latestVersion.jsFileUrl, globalName, skinId);

      return Component;
      
    } catch (error) {
      console.error(`[loadApiBasedSkin] API 기반 스킨 로드 실패: ${skinId}`, error);
      throw error;
    }
  }); // skinData 매개변수 제거
}

/**
 * Create a wrapper component that loads external skin on demand
 * @param {string} skinId 
 * @returns {React.ComponentType}
 */
export function createExternalSkinWrapper(skinId) {
  // Check cache first
  if (externalSkinWrapperCache.has(skinId)) {
    return externalSkinWrapperCache.get(skinId);
  }
  
  // ErrorBoundary class component to catch React rendering errors
  class SkinErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false, error: null, errorInfo: null };
    }
    
    static getDerivedStateFromError(error) {
      return { hasError: true };
    }
    
    componentDidCatch(error, errorInfo) {
      console.error('[SkinErrorBoundary] Caught rendering error:', {
        skinId: this.props.skinId,
        error,
        errorInfo
      });
      this.setState({
        error,
        errorInfo
      });
    }
    
    render() {
      if (this.state.hasError) {
        return (
          <div style={{ 
            padding: '20px', 
            background: '#fee', 
            border: '1px solid #fcc',
            borderRadius: '8px',
            color: '#c00'
          }}>
            <strong>외부 스킨 렌더링 오류 (React Error Boundary)</strong>
            <div style={{ marginTop: '10px', fontSize: '14px' }}>
              스킨 ID: {this.props.skinId}<br />
              오류: {this.state.error && this.state.error.toString()}<br />
              <details style={{ marginTop: '10px' }}>
                <summary style={{ cursor: 'pointer' }}>상세 정보</summary>
                <pre style={{ 
                  marginTop: '10px', 
                  padding: '10px', 
                  background: '#f5f5f5',
                  overflow: 'auto',
                  fontSize: '12px'
                }}>
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            </div>
          </div>
        );
      }
      
      return this.props.children;
    }
  }
  
  // Create a memoized wrapper component
  const ExternalSkinWrapper = memo(function ExternalSkinWrapper(props) {
    const [state, setState] = useState({
      component: null,
      loading: true,
      error: null
    });
    const [showLoading, setShowLoading] = useState(false);
    
    // 즉시 로드 (Intersection Observer 제거)
    useEffect(() => {
      let mounted = true;

      // 100ms 후에만 로딩 메시지 표시 (빠른 로딩 시 깜빡임 방지)
      const loadingTimer = setTimeout(() => {
        if (mounted && state.loading) {
          setShowLoading(true);
        }
      }, 100);
      

      loadComponentSkin(skinId)
        .then(LoadedComponent => {
          if (mounted) {
            setState({
              component: LoadedComponent,
              loading: false,
              error: null
            });
            clearTimeout(loadingTimer);
            setShowLoading(false);
            // Enable CSS for this skin when loaded
            enableSkinCSS(skinId);
          }
        })
        .catch(err => {
          if (mounted) {
            console.error(`[ExternalSkinWrapper] 스킨 로드 실패: ${skinId}`, err);
            setState({
              component: null,
              loading: false,
              error: err
            });
            clearTimeout(loadingTimer);
            setShowLoading(false);
          }
        });
      
      return () => {
        mounted = false;
        clearTimeout(loadingTimer);
        // Disable CSS for this skin when unmounted
        disableSkinCSS(skinId);
      };
    }, [skinId]); // shouldLoad 제거

    // 로딩 중
    if (state.loading && showLoading) {
      return (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}
        >
          <div>컴포넌트 스킨 로딩 중...</div>
        </div>
      );
    }
    
    // 로딩 중이지만 아직 로딩 메시지를 표시하지 않는 상태
    if (state.loading) {
      return null; // 빈 상태로 대기
    }
    
    // 에러 발생
    if (state.error) {
      return (
        <div style={{ 
          padding: '20px', 
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          color: '#c00'
        }}>
          <strong>스킨 로드 실패</strong>
          <div style={{ marginTop: '10px', fontSize: '14px' }}>
            {state.error.message}
          </div>
        </div>
      );
    }
    
    const SkinComponent = state.component;
    
    if (!SkinComponent) {
      return null;
    }
    
    // SkinComponent가 default export를 가진 모듈인 경우 처리
    const ActualComponent = (SkinComponent && typeof SkinComponent.default === 'function') 
      ? SkinComponent.default 
      : SkinComponent;
    
    // 컴포넌트가 함수가 아닌 경우 경고
    if (typeof ActualComponent !== 'function') {
      console.error('[ExternalSkinWrapper] 스킨 컴포넌트가 유효한 React 컴포넌트가 아닙니다:', {
        skinId,
        type: typeof ActualComponent,
        value: ActualComponent
      });
      return (
        <div style={{ 
          padding: '20px', 
          background: '#fee', 
          border: '1px solid #fcc',
          borderRadius: '8px',
          color: '#c00'
        }}>
          <strong>스킨 컴포넌트 오류</strong>
          <div style={{ marginTop: '10px', fontSize: '14px' }}>
            스킨 ID: {skinId}<br />
            오류: 유효한 React 컴포넌트가 아닙니다 (type: {typeof ActualComponent})
          </div>
        </div>
      );
    }
    
    return (
      <div className={`external-skin-wrapper skin-${skinId}`} data-skin-id={skinId}>
        <SkinErrorBoundary skinId={skinId}>
          <ActualComponent {...props} />
        </SkinErrorBoundary>
      </div>
    );
  });
  
  // Cache the wrapper component
  externalSkinWrapperCache.set(skinId, ExternalSkinWrapper);
  
  return ExternalSkinWrapper;
}

/**
 * Enable CSS for a specific skin
 * @param {string} skinId 
 */
export function enableSkinCSS(skinId) {
  if (!skinId) return;
  
  activeSkins.add(skinId);
  
  // Find all CSS link elements for this skin and enable them
  const links = document.querySelectorAll(`link[data-skin-id="${skinId}"]`);
  links.forEach(link => {
    link.disabled = false;
  });
}

/**
 * Disable CSS for a specific skin
 * @param {string} skinId 
 */
export function disableSkinCSS(skinId) {
  if (!skinId) return;
  
  activeSkins.delete(skinId);
  
  // Find all CSS link elements for this skin and disable them
  const links = document.querySelectorAll(`link[data-skin-id="${skinId}"]`);
  links.forEach(link => {
    link.disabled = true;
  });
}

/**
 * Disable all external skin CSS except the active ones
 */
export function updateExternalSkinCSS() {
  // Find all external skin CSS
  const allSkinLinks = document.querySelectorAll('link[data-skin-css="true"]');
  
  allSkinLinks.forEach(link => {
    const skinId = link.getAttribute('data-skin-id');
    if (skinId) {
      // Disable if not in active skins
      link.disabled = !activeSkins.has(skinId);
    }
  });
}

// All functions are already exported as named exports above